<script setup lang="ts">
import { computed } from "vue";
import Lucide from "@/components/Base/Lucide";

export type PageTitleVariant =
  | "indigo"
  | "emerald"
  | "violet"
  | "amber"
  | "rose"
  | "cyan"
  | "orange"
  | "pink"
  | "slate";

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    icon: string;
    variant?: PageTitleVariant;
    heading?: "h1" | "h2" | "h3";
  }>(),
  {
    variant: "indigo",
    heading: "h1",
  }
);

const variantStyles: Record<PageTitleVariant, string> = {
  indigo: "text-indigo-600",
  emerald: "text-emerald-600",
  violet: "text-violet-600",
  amber: "text-amber-600",
  rose: "text-rose-600",
  cyan: "text-cyan-600",
  orange: "text-orange-600",
  pink: "text-pink-600",
  slate: "text-slate-600",
};

const colorClass = computed(() => variantStyles[props.variant]);

const iconSizeClass = computed(() => {
  switch (props.heading) {
    case "h2":
      return "w-7 h-7";
    case "h3":
      return "w-6 h-6";
    default:
      return "w-8 h-8";
  }
});

const titleSizeClass = computed(() => {
  switch (props.heading) {
    case "h2":
      return "text-xl font-bold";
    case "h3":
      return "text-lg font-bold";
    default:
      return "text-2xl font-bold";
  }
});
</script>

<template>
  <div class="min-w-0">
    <component
      :is="heading"
      class="flex flex-wrap items-center gap-x-3 gap-y-1"
    >
      <Lucide
        :icon="icon"
        class="stroke-[1.3] flex-shrink-0"
        :class="[iconSizeClass, colorClass]"
      />
      <span :class="[titleSizeClass, colorClass]">{{ title }}</span>
      <slot name="title-extra" />
    </component>
    <p v-if="subtitle" class="text-sm text-slate-500 mt-0.5">{{ subtitle }}</p>
    <slot name="subtitle-extra" />
  </div>
</template>
